import { DefaultUrlSerializer } from "@angular/router";
import { forEach, replace } from "lodash-es";

const serializer = new DefaultUrlSerializer();

/**
 * Replaces the current fragment with the new fragment.
 * @example
 * // returns "/en/home#header"
 * replaceFragment("/en/home#footer", "header");
 */
export function replaceFragment(url: string | undefined, fragment: string | undefined): string {
	const urlWithoutFragment = url?.split("#")[0] || "";
	const normalizedFragment = normalizeFragment(fragment);
	return normalizedFragment ? `${urlWithoutFragment}#${normalizedFragment}` : urlWithoutFragment;
}

/**
 * Returns the fragment from the path.
 * @example
 * // returns "footer"
 * getFragment("/en/home#footer");
 * * @example
 * // returns undefined
 * getFragment("/en/home");
 */
export function getFragment(url: string | undefined): string | undefined {
	if (!url) {
		return undefined;
	}

	return url.split("#")[1];
}

/**
 * Returns the fragment without hash.
 * @example
 * // returns "footer"
 * getFragmentWithoutHash("terms-and-conditions#footer");
 * @example
 * // returns "footer"
 * getFragmentWithoutHash("#footer");
 * @example
 * // returns "terms-and-conditions"
 * getFragmentWithoutHash("terms-and-conditions");
 * @example
 * // returns ""
 * getFragmentWithoutHash("terms-and-conditions", true);
 */
export function normalizeFragment(fragment: string | undefined | null, isFullUrl = false): string {
	if (!fragment) {
		return "";
	}

	const index = fragment.indexOf("#");
	if (index === -1 && isFullUrl) {
		return "";
	}

	return fragment.substring(index + 1);
}

/**
 * Returns the path without fragment and query string (same as native `location.pathname`)
 */
export function getPathName(path: string): string {
	return replace(path, /(\?|#)(.*)$/, "");
}

/**
 * Returns the path with the new query params
 */
export function replaceQueryParams(path: string, queryParams: Record<string, any>) {
	const tree = serializer.parse(path);
	tree.queryParams = queryParams;
	return serializer.serialize(tree);
}

/**
 * Returns the parsed query params from the path
 */
export function getQueryParams(path: string): Record<string, any> {
	return serializer.parse(replace(path, /(.*)(\?)/, "?")).queryParams;
}

/**
 * Returns the parsed config overrides from the query parameters
 */
export function parseConfigOverrideString(queryParameters: Record<string, any>): Record<string, any> {
	const configOverrides: Record<string, any> = {};
	const configOverrideString = queryParameters?.configOverride;

	if (!configOverrideString || !/^\[\([^\(\)]+\)(,\([^\(\)]+\))*\]$/g.test(configOverrideString)) {
		return configOverrides;
	}

	const configOverrideStrings = configOverrideString.match(/\([^\(\)]+\)/g);
	forEach(configOverrideStrings, (value) => {
		const [paramKey, paramValue] = value.substring(1, value.length - 1).split("=");
		try {
			configOverrides[paramKey] = JSON.parse(paramValue);
		} catch {
			/* Noop */
		}
	});

	return configOverrides;
}
